<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import Pagination from '$lib/Pagination.svelte';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import TimeTravelModal from '$lib/TimeTravelModal.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faChevronUp, faChevronDown, faRotate, faTrash, faMerge, faTrashCan, faCodeMerge, faBinary, faClock, faFileExport } from '@fortawesome/pro-light-svg-icons';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import { userRoles, userReadOnly } from '$stores/sessionStore';
  import { studyProperties } from '$stores/configStore';
  import { hasAccessUiAction } from '$utils/permissionUtils.js';
  import { formatToLocalDateTime } from '$utils/dateUtils.js';
  
  // Use local API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';

  let stats = [];
  let limit = 20;
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let isLoading = true;

  let currentDateTime = new Date().toLocaleString();

  // Time Travel Modal state
  let showTimeTravelModal = false;
  let selectedSensorForTimeTravel = null;

  $: canExportGlucoseJson = hasAccessUiAction(
    'subjectZeros.exportGlucoseJson',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canMergeActivities = hasAccessUiAction(
    'subjectZeros.mergeActivities',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canTimeTravelSensor = hasAccessUiAction(
    'subjectZeros.timeTravelSensor',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canFixZeroActivityGuid = hasAccessUiAction(
    'subjectZeros.fixZeroActivityGuid',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canDeleteActivities = hasAccessUiAction(
    'subjectZeros.deleteActivities',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );

  onMount(async () => {
    //selectedStudy.set(null);
    //selectedSite.set(null);
    //selectedParticipant.set(null);

    if ($selectedStudy) {
      fetchStats(1);
    }
  });

  function handleParticipantClick(site, person) {
    selectedSite.set(site);
    selectedParticipant.set(person);
    goto(`/person`);
  }

  async function handleDeleteClick(stat) {
    const confirmed = confirm(
      `Are you sure you want to delete Activity: ${stat.activityGUID}?\nBe aware this does not delete from SUMMA.`
    );

    if (confirmed) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/stats/subjectWithZeros?subjectActivityGUID=${stat.activityGUID}`,
          {
            method: 'DELETE'
          }
        );

        if (!response.ok) {
          throw new Error('Failed to delete activity');
        }

        // Refresh the data after successful delete
        await fetchStats();
        alert('Activity deleted successfully');
      } catch (error) {
        console.error('Error deleting activity:', error);
        alert('Failed to delete activity');
      }
    }
  }

  async function handleFixZeroesGUIDClick(stat) {
    const confirmed = confirm(
      `Are you sure you want to update glucose readings?\n\nActivity: 000000000000000000000000\nNew Activity: ${stat.activityGUID}\nDeviceId: ${stat.deviceId}`
    );

    if (confirmed) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/stats/updateMany?subjectGUID=${stat.subjectGUID}&oldActivityGUID=000000000000000000000000&newActivityGUID=${stat.activityGUID}&oldDeviceId=${stat.deviceId}`
        );

        if (!response.ok) {
          throw new Error('Failed to update activity');
        }

        // Refresh the data after successful update
        await fetchStats();
        alert('Activity updated successfully');
      } catch (error) {
        console.error('Error updating activity:', error);
        alert('Failed to update activity');
      }
    }
  }

  async function handleMergeActivitiesClick(stat, index) {
    // Find the previous stats entry
    if (index === 0 || !stats[index - 1]) {
      alert('No previous activity found to merge with.');
      return;
    }

    const previousStat = stats[index - 1];

    const confirmed = confirm(
      `Are you sure you want to merge these activities?\n\n` +
      `From:\n` +
      `- Activity: ${stat.activityGUID}\n` +
      `- Device: ${stat.deviceId}\n\n` +
      `To:\n` +
      `- Activity: ${previousStat.activityGUID}\n` +
      `- Device: ${previousStat.deviceId}`
    );

    if (confirmed) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/stats/updateMany?` +
          `subjectGUID=${stat.subjectGUID}` +
          `&oldActivityGUID=${stat.activityGUID}` +
          `&newActivityGUID=${previousStat.activityGUID}` +
          `&oldDeviceId=${stat.deviceId}` +
          `&newDeviceId=${previousStat.deviceId}`
        );

        if (!response.ok) {
          throw new Error('Failed to merge activities');
        }

        // Refresh the data after successful update
        await fetchStats();
        alert('Activities merged successfully');
      } catch (error) {
        console.error('Error merging activities:', error);
        alert('Failed to merge activities');
      }
    }
  }

  function handleTimeTravelClick(stat) {
    selectedSensorForTimeTravel = stat;
    showTimeTravelModal = true;
  }

  async function handleTimeTravelConfirm(event) {
    const { targetDateTime, timezone } = event.detail;

    try {
      const response = await fetch(`${API_BASE_URL}/stats/timeTravelSensor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subjectGUID: selectedSensorForTimeTravel.subjectGUID,
          deviceId: selectedSensorForTimeTravel.deviceId,
          activityGUID: selectedSensorForTimeTravel.activityGUID || null,
          targetDateTime: targetDateTime,
          timezone: timezone
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Backend error:', errorData);
        throw new Error(errorData.message || errorData.error || 'Failed to time travel sensor data');
      }

      const result = await response.json();

      // Refresh the data after successful time travel
      await fetchStats();
      
      alert(
        `Time travel completed successfully!\n\n` +
        `Modified ${result.modifiedCount} readings\n` +
        `Backup created: ${result.backupCount} readings\n` +
        `Time offset: ${result.offsetDays} days\n` +
        `Timezone: ${result.timezoneCode}`
      );
    } catch (error) {
      console.error('Error in time travel:', error);
      alert(`Time travel error: ${error.message}`);
    }
  }

  async function handleExportClick(stat) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/stats/exportGlucoseReadings?subjectGUID=${stat.subjectGUID}&activityGUID=${stat.activityGUID}&deviceId=${stat.deviceId}`
      );

      if (!response.ok) {
        throw new Error('Failed to export glucose readings');
      }

      const data = await response.json();

      // Create a filename with subject ID, device ID, and timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const filename = `glucose-readings_${stat.subjectId}_${stat.deviceId}_${timestamp}.json`;

      // Convert data to JSON string with proper formatting
      const jsonString = JSON.stringify(data, null, 2);

      // Create a blob and download link
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      alert(`Successfully exported ${data.metadata.recordCount} glucose readings to ${filename}`);
    } catch (error) {
      console.error('Error exporting glucose readings:', error);
      alert('Failed to export glucose readings');
    }
  }

  function handleRefresh() {
    fetchStats(1);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchStats(newPage);
    }
  }

  async function fetchStats() {
    try {
      isLoading = true; // Set loading to true before starting the fetch

      const res = await fetch(
        `${API_BASE_URL}/stats/subjectWithZeros?subjectGUID=${$selectedParticipant._id}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch stats');
      }

      const responseData = await res.json();

      //console.info('responseData:', responseData);

      resultCount = responseData.length;

      stats = responseData;

      //console.info('Stats fetched:', stats);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      isLoading = false; // Set loading to false after fetch is complete
    }
  }

  // Function to calculate time difference in days and hours
  function calculateTimeDifference(dateString) {
    const now = new Date();
    const pastDate = new Date(dateString);
    const diffMs = now - pastDate;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    // Return empty string if the difference is less than an hour
    if (diffDays === 0 && diffHours === 0) {
      return '';
    }

    // Return only the days part if the difference is a day or more
    if (diffDays >= 1) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
    }

    // Return the hours part if the difference is less than a day
    return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
  }
</script>

<StudyHeader />

<FeatureInfo 
  title="CGM Activities" 
  description="This page displays continuous glucose monitoring (CGM) activities for the selected participant. You can view device IDs, activity identifiers, 
  reading counts, and timestamp information. Administrator roles can export glucose readings to JSON files for backup or import purposes, and perform data corrections like merging activities or deleting activities without readings."
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{/if}

<!-- Title and Filters -->
<Toolbar title="CGM Activities" itemCountTitle="Activities" itemCount={resultCount}>
  <div class="flex items-center gap-4">
    <div class="text-black dark:text-white">
      <button on:click={() => handleRefresh()} class="pagination mx-1 p-0">
        <FontAwesomeIcon icon={faRotate} />
      </button>
    </div>
  </div></Toolbar
>

{#if isLoading}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader mb-4"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">
      preparing subject zeros report
    </p>
  </div>
{:else}
  <div class="flex justify-between pt-0 ph-2 pb-0 px-1">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Subject</th>
          <th class="px-4 py-2 text-left bold">Subject GUID</th>
          <th class="px-4 py-2 text-left bold">Device Id</th>
          <th class="px-4 py-2 text-left bold">Activity GUID</th>
          <th class="px-4 py-2 text-left bold">Count</th>
          <th class="px-4 py-2 text-left bold">First (Local)</th>
          <th class="px-4 py-2 text-left bold">Last (Local)</th>
          <th class="w-px px-4 py-2 text-left bold">Actions</th>
        </tr>
      </thead>
      <tbody>
        {#each stats as stat, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2">{stat.subjectId}</td>
            <td class="px-4 py-2">{stat.subjectGUID}</td>
            <td class="px-4 py-2">{stat.deviceId || ''}</td>
            <td class="px-4 py-2">{stat.activityGUID}</td>
            <td class="px-4 py-2">{stat.readingCount}</td>
            <td class="px-4 py-2">{stat.firstReading ? formatToLocalDateTime(stat.firstReading, $selectedParticipant?.timezoneCode || 'UTC') : ''}</td>
            <td class="px-4 py-2">{stat.lastReading ? formatToLocalDateTime(stat.lastReading, $selectedParticipant?.timezoneCode || 'UTC') : ''}</td>
            <td class="px-4 py-2 w-40">
              <button 
                on:click={() => handleExportClick(stat)} 
                class="pagination mx-1 p-0"
                disabled={!canExportGlucoseJson || stat.readingCount === 0}
                class:opacity-50={!canExportGlucoseJson || stat.readingCount === 0}
                title={!canExportGlucoseJson || stat.readingCount === 0 ? 'Not available' : 'Export glucose readings to JSON'}
              >
                <FontAwesomeIcon icon={faFileExport} class="text-sky-800 dark:text-white/70" />
              </button> 
              <button 
                on:click={() => handleMergeActivitiesClick(stat, index)} 
                class="pagination mx-1 p-0"
                disabled={!canMergeActivities}
                class:opacity-50={!canMergeActivities}
                title={!canMergeActivities ? 'Not available' : 'Merge activities'}
              >
                <FontAwesomeIcon icon={faMerge} class="text-sky-800 dark:text-white/70" />
              </button> 
              <button 
                on:click={() => handleTimeTravelClick(stat)} 
                class="pagination mx-1 p-0"
                disabled={!canTimeTravelSensor || stat.readingCount === 0}
                class:opacity-50={!canTimeTravelSensor || stat.readingCount === 0}
                title={!canTimeTravelSensor || stat.readingCount === 0 ? 'Not available' : 'Time travel sensor data'}
              >
                <FontAwesomeIcon icon={faClock} class="text-sky-800 dark:text-white/70" />
              </button> 
              <button 
                on:click={() => handleFixZeroesGUIDClick(stat)} 
                class="pagination mx-1 p-0"
                disabled={!canFixZeroActivityGuid || stat.activityGUID != '000000000000000000000000'}
                class:opacity-50={!canFixZeroActivityGuid || stat.activityGUID != '000000000000000000000000'}
                title={!canFixZeroActivityGuid || stat.activityGUID != '000000000000000000000000' ? 'Not available' : 'Fix 0000s'}
              >
                <FontAwesomeIcon icon={faBinary} class="text-sky-800 dark:text-white/70" />
              </button> 
              <button 
                on:click={() => handleDeleteClick(stat)} 
                class="pagination mx-1 p-0"
                disabled={!canDeleteActivities || stat.readingCount > 0}
                class:opacity-50={!canDeleteActivities || stat.readingCount > 0}
                title={!canDeleteActivities || stat.readingCount > 0 ? 'Not available' : 'Delete activities'}
              >
                <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
              </button> 
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
</Footer>

<TimeTravelModal 
  bind:show={showTimeTravelModal}
  sensorInfo={selectedSensorForTimeTravel}
  participantTimezone={$selectedParticipant?.timezoneCode || 'UTC'}
  firstReadingDate={selectedSensorForTimeTravel?.firstReading}
  on:confirm={handleTimeTravelConfirm}
  on:close={() => showTimeTravelModal = false}
/>

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
